import { action, observable, computed, makeObservable } from 'mobx'
import _ from 'lodash'
import React from 'react'
import { move } from '@dnd-kit/helpers'
import api from 'api'
import { templates, LANDSCAN_LATEST_YEAR } from '../constants'
import TargetGroup from './TargetGroup'
import Leaflet from 'leaflet'
import { catchError } from './ErrorStore'
import { alignToStep } from '../utils'
import PointOfInterest from '../stores/PointOfInterest'
import ColorIcon from '../components/common/icons/ColorIcon'

const TARGET_GROUP_PROPERTIES = ['pk', 'name', 'owner', 'is_owner', 'share_type', 'modified', 'folder_status', 'status_color', 'additional_info', 'contact_detail', 'tag_ids']

class DataStore {
  @observable rawTargetGroupsData = []
  @observable targetGroups = []
  @observable markerColors = []
  @observable recentColors = []
  @observable shapefileCount
  @observable regionsCount
  @observable markerfileCount
  @observable selectedCopyGroup
  @observable addTargetGroupForm = { name: '', error: null }
  @observable enableBoundaries = true
  @observable sortConfig = { attr: 'name', dir: 'asc' }
  @observable poi = new PointOfInterest()
  @observable defaultYear = { result: 2024 }
  @observable defaultDsName = 'landscan'
  @observable landscanLatestYear = LANDSCAN_LATEST_YEAR
  @observable shapeStatuses = []
  @observable folderStatuses = []
  @observable userTags = []
  @observable userAdditionalInfoItems = []
  @observable statusColorType = 'fill'

  constructor () {
    makeObservable(this)
  }

  @computed get tagOptions () {
    if (this.userTags.length > 0) {
      return this.userTags.map(tag => ({
        id: tag.id,
        value: tag.name,
        label: tag.name,
        color: tag.color,
        icon: React.createElement(ColorIcon, { fill: tag.color, size: 12, outline: tag.color })
      }))
    }
    return [{
      id: -999,
      value: '',
      label: 'Please configure statuses in Edit → Tags.',
      isDisabled: true
    }]
  }

  @computed get statusOptions () {
    return [
      { value: '', label: 'All' },
      ...this.shapeStatuses.map(status => ({
        id: status.id,
        value: status.name,
        label: status.name
      }))
    ]
  }

  @computed get copyGroupList () {
    return this.targetGroups.map((group) => {
      return { value: group.pk, label: group.is_owner ? group.name : `${group.name} (shared)` }
    })
  }

  @computed get copyDestinationGroup () {
    if (!this.selectedCopyGroup) {
      return null
    }
    return _.find(this.targetGroups, { pk: this.selectedCopyGroup.value })
  }

  @computed get myTargetGroups () {
    return _.filter(this.targetGroups, { is_owner: true })
  }

  @computed get targetGroupCount () {
    return this.myTargetGroups.length
  }

  @computed get sharedTargetGroups () {
    return _.filter(this.targetGroups, { is_owner: false })
  }

  sortTargetGroups (sorting) {
    this.sortConfig.dir = sorting.dir
    this.sortConfig.attr = sorting.attr || this.sortConfig.attr

    const sorted = this.targetGroups.slice().sort((a, b) => {
      const valA = String(a[this.sortConfig.attr] || '')
      const valB = String(b[this.sortConfig.attr] || '')
      const comparison = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
      return this.sortConfig.dir === 'desc' ? -comparison : comparison
    })

    this.targetGroups.replace(sorted)
  }

  sortByModified () {
    const sorted = this.targetGroups.slice().sort((a, b) => {
      const dateA = new Date(a.modified || a.created || 0)
      const dateB = new Date(b.modified || b.created || 0)
      return dateB - dateA
    })
    this.targetGroups.replace(sorted)
  }

  applyPersistedSort () {
    const savedSort = localStorage.getItem('leftDrawerSortType') || 'ASC'
    if (savedSort === 'ASC') {
      this.sortTargetGroups({ attr: 'name', dir: 'asc', is_owner: true })
    } else if (savedSort === 'DESC') {
      this.sortTargetGroups({ attr: 'name', dir: 'desc', is_owner: true })
    } else {
      this.sortByModified()
    }
  }

  @catchError async loadTargetGroups () {
    let response = await api.getTargetGroups()
    let groups = response.data

    // If a user has no target groups, create a default one
    if (_.filter(groups, { is_owner: true }).length === 0 || _.filter(groups, { is_owner: true }).length === 1) {
      await api.postTargetGroup({
        name: this.newTargetGroupName(groups),
        text: ''
      })

      response = await api.getTargetGroups()
      groups = response.data
    }

    this.rawTargetGroupsData = groups

    await this.mergeTargetGroups(groups)
    await this.loadUserStatusConfiguration()
  }

  async mergeTargetGroups (groups) {
    // Isolate per-group failures so one bad target group can't wipe out
    // the whole list (this.targetGroups would otherwise never get reassigned).
    const mergedGroups = await Promise.all(_.map(groups, async remoteGroup => {
      try {
        let localGroup = _.find(this.targetGroups, { pk: remoteGroup.pk })
        const remoteProperties = _.pick(remoteGroup, TARGET_GROUP_PROPERTIES)

        if (!localGroup) {
          localGroup = new TargetGroup(remoteProperties, this.enableBoundaries)
          await localGroup.load()
        } else {
          // Update existing observable properties manually (extendObservable doesn't update existing observables)
          localGroup.pk = remoteProperties.pk
          localGroup.name = remoteProperties.name
          localGroup.owner = remoteProperties.owner
          localGroup.is_owner = remoteProperties.is_owner
          localGroup.share_type = remoteProperties.share_type
          localGroup.modified = remoteProperties.modified
          localGroup.folder_status = remoteProperties.folder_status
          localGroup.status_color = remoteProperties.status_color
          localGroup.additional_info = remoteProperties.additional_info
          localGroup.contact_detail = remoteProperties.contact_detail
          localGroup.tag_ids = remoteProperties.tag_ids
        }

        return localGroup
      } catch (e) {
        console.error('Failed to load target group', remoteGroup && remoteGroup.pk, e)
        return null
      }
    }))
    this.targetGroups = _.compact(mergedGroups)
    this.applyPersistedSort()
  }

  @catchError async saveUserAdditionalInfoItem (data) {
    const response = await api.createUserAdditionalInfoItem(data)
    this.setUserAdditionalInfoItems(this.userAdditionalInfoItems.concat(response.data))
    return response.data
  }

  // Updates the global item definition (e.g. its title) shared by every shape, marker, folder and boundary.
  async updateUserAdditionalInfoItem (id, data) {
    const previous = this.userAdditionalInfoItems
    this.setUserAdditionalInfoItems(previous.map(item => (
      item.id === id ? { ...item, ...data, input: { ...item.input, ...data.input } } : item
    )))
    try {
      const response = await api.updateUserAdditionalInfoItem(id, data)
      this.setUserAdditionalInfoItems(this.userAdditionalInfoItems.map(item => item.id === id ? response.data : item))
    } catch (error) {
      console.error('Failed to update additional info item', error)
      this.setUserAdditionalInfoItems(previous)
    }
  }

  // Sidebar drag and drop (dnd-kit event).
  sortUserAdditionalInfoItems (event) {
    return this.reorderUserAdditionalInfoItems(move(this.userAdditionalInfoItems, event).map(item => item.id))
  }

  // Reorder locally right away so the drop doesn't snap back, then persist the full order.
  async reorderUserAdditionalInfoItems (ids) {
    const previous = this.userAdditionalInfoItems
    if (ids.every((id, index) => previous[index] && previous[index].id === id)) return
    const byId = _.keyBy(previous, 'id')
    this.setUserAdditionalInfoItems(ids.map(id => byId[id]))
    try {
      const response = await api.reorderUserAdditionalInfoItems({ ids })
      this.setUserAdditionalInfoItems(response.data)
    } catch (error) {
      console.error('Failed to save additional info item order', error)
      this.setUserAdditionalInfoItems(previous)
    }
  }

  @catchError async deleteUserAdditionalInfoItem (id) {
    await api.deleteUserAdditionalInfoItem(id)
    this.removeUserAdditionalInfoItem(id)
  }

  // Mirrors the server-side cascade so already-loaded objects drop their values for this item.
  @action removeUserAdditionalInfoItem (id) {
    this.userAdditionalInfoItems = this.userAdditionalInfoItems.filter(item => Number(item.id) !== Number(id))
    const keep = entry => Number(entry.info_item) !== Number(id)
    this.targetGroups.forEach(group => {
      [group, ...group.geopolygons, ...group.boundaries, ...group.zipCodeBoundaries, ...group.savedMarkers].forEach(owner => {
        if (Array.isArray(owner.additional_info)) owner.additional_info = owner.additional_info.filter(keep)
      })
    })
  }

  async loadUserStatusConfiguration () {
    try {
      // Keep status definitions and their global display mode in one MobX snapshot.
      const responses = await Promise.all([
        api.getShapeStatuses(),
        api.getFolderStatuses(),
        api.getUserConfigurations(),
        api.getUserTags(),
        api.getUserAdditionalInfoItems(),
      ])
      this.setStatuses('shape', responses[0].data)
      this.setStatuses('folder', responses[1].data)
      this.setStatusColorType(responses[2].data.status_color_type)
      this.setUserTags(responses[3].data)
      this.setUserAdditionalInfoItems(responses[4].data)
    } catch (error) {
      console.error('Failed to load user status configuration', error)
    }
  }

  @action setStatuses (type, statuses) {
    const items = Array.isArray(statuses) ? statuses : []
    if (type === 'shape') {
      this.shapeStatuses = items
    } else {
      this.folderStatuses = items
    }
  }

  @action setStatusColorType (colorType) {
    this.statusColorType = colorType === 'outline' ? 'outline' : 'fill'
  }

  @action setUserTags (userTags) {
    this.userTags = userTags
  }

  @action setUserAdditionalInfoItems (userAdditionalInfoItems) {
    this.userAdditionalInfoItems = userAdditionalInfoItems
  }

  setFilterMaxAndMins (uiStore) {
    let minPopulation = Infinity
    let maxPopulation = -Infinity
    let minAvgAge = Infinity
    let maxAvgAge = -Infinity
    let minDensity = Infinity
    let maxDensity = -Infinity
    let minGenderRatio = Infinity
    let maxGenderRatio = -Infinity
    let maxHhSize = 0
    let hasHhSize = false

    this.targetGroups.forEach(group => {
      const allRegions = [
        ...(group.geopolygons || []),
        ...(group.boundaries || []),
        ...(group.zipCodeBoundaries || [])
      ]
      allRegions.forEach(region => {
        if (!region || !region.analysis) return
        const { sum: population, avgAge, density, genderRatio, hh_size, hh_income } = region.analysis

        // Population
        if (typeof population === 'number') {
          if (population < minPopulation) minPopulation = population
          if (population > maxPopulation) maxPopulation = population
        }

        // Average Age
        if (typeof avgAge === 'number') {
          if (avgAge < minAvgAge) minAvgAge = avgAge
          if (avgAge > maxAvgAge) maxAvgAge = avgAge
        }

        // Density
        if (typeof density === 'number') {
          if (density < minDensity) minDensity = density
          if (density > maxDensity) maxDensity = density
        }

        // Gender ratio
        if (typeof genderRatio === 'number') {
          if (genderRatio < minGenderRatio) minGenderRatio = genderRatio
          if (genderRatio > maxGenderRatio) maxGenderRatio = genderRatio
        }

        // Household size / Income for Income filter
        let val = null
        if (typeof hh_size === 'number' && !isNaN(hh_size) && hh_size > 0) {
          val = hh_size
        } else if (region.population_datas) {
          const picked = region.pickPopulationDataForCurrentYear ? region.pickPopulationDataForCurrentYear(region.population_datas) : (Array.isArray(region.population_datas) ? region.population_datas[0] : null)
          if (picked) {
            if (typeof picked.hh_size === 'number' && !isNaN(picked.hh_size) && picked.hh_size > 0) {
              val = picked.hh_size
            }
          }
        }

        if (typeof val === 'number' && !isNaN(val) && val > 0) {
          hasHhSize = true
          if (val > maxHhSize) {
            maxHhSize = val
          }
        }
      })
    })

    if (minPopulation === Infinity) minPopulation = null
    if (maxPopulation === -Infinity) maxPopulation = null
    if (minAvgAge === Infinity) minAvgAge = null
    if (maxAvgAge === -Infinity) maxAvgAge = null
    if (minDensity === Infinity) minDensity = null
    if (maxDensity === -Infinity) maxDensity = null
    if (minGenderRatio === Infinity) minGenderRatio = null
    if (maxGenderRatio === -Infinity) maxGenderRatio = null

    const safeHhSizeMax = hasHhSize ? (Math.round(maxHhSize) + 10) : 10

    // Steps for each filter
    const popStep = 1
    const ageStep = 0.1
    const densityStep = 1
    const ratioStep = 0.01

    // Align min/max to step so the slider can hit them exactly
    const newPopulationRange = [
      alignToStep(minPopulation, popStep, 'floor'),
      alignToStep(maxPopulation, popStep, 'ceil')
    ]
    const newAvgAgeRange = [
      alignToStep(minAvgAge, ageStep, 'floor'),
      alignToStep(maxAvgAge, ageStep, 'ceil')
    ]
    const newDensityRange = [
      alignToStep(minDensity, densityStep, 'floor'),
      alignToStep(maxDensity, densityStep, 'ceil')
    ]
    const newGenderRatioRange = [
      alignToStep(minGenderRatio, ratioStep, 'floor'),
      alignToStep(maxGenderRatio, ratioStep, 'ceil')
    ]
    const newHhSizeRange = [
      0,
      safeHhSizeMax
    ]

    // Update the default values and current values
    uiStore.filters.population._defaultValue = newPopulationRange.slice()
    uiStore.filters.density._defaultValue = newDensityRange.slice()
    uiStore.filters.avgAge._defaultValue = newAvgAgeRange.slice()
    uiStore.filters.genderRatio._defaultValue = newGenderRatioRange.slice()
    if (uiStore.filters.income) {
      uiStore.filters.income.setDisabled(!hasHhSize)
      uiStore.filters.income._defaultValue = newHhSizeRange.slice()
    }

    uiStore.filters.population.setValue(newPopulationRange)
    uiStore.filters.density.setValue(newDensityRange)
    uiStore.filters.avgAge.setValue(newAvgAgeRange)
    uiStore.filters.genderRatio.setValue(newGenderRatioRange)
    if (uiStore.filters.income) {
      uiStore.filters.income.setValue(newHhSizeRange)
    }
  }

  reloadTargetGroups (ids) {
    ids.forEach(async (id) => {
      const targetGroup = this.myTargetGroups.find(group => group.pk === id)

      if (!targetGroup) return

      await targetGroup.load()
    })
  }

  // Generate a default target group name like "Target Group 2"
  newTargetGroupName (groups) {
    let i = 1
    while (_.find(groups, {
      name: templates.TARGET_GROUP_NAME(i)
    })) {
      i++
    }

    return templates.TARGET_GROUP_NAME(i)
  }

  async renameTargetGroup (group, name) {
    await group.rename(name)
    await this.loadTargetGroups()
  }

  initializeAddTargetGroupForm () {
    this.addTargetGroupForm.name = this.newTargetGroupName(this.targetGroups)
  }

  @catchError async addTargetGroup (group) {
    const response = await api.addTargetGroup(group)
    await this.loadTargetGroups()
    return response.data.pk
  }

  @catchError async deleteTargetGroup (group) {
    await api.deleteTargetGroup(group.pk)
    await this.loadTargetGroups()
  }

  async toggleTargetGroupShare (group) {
    if (group.share_type === 'none') {
      await group.share()
    } else {
      await group.unshare()
    }
    await this.loadTargetGroups()
  }

  async refreshRecentColors () {
    const response = await api.getRecentColors(5)
    this.recentColors = response.data
  }

  bounds () {
    const bounds = Leaflet.latLngBounds()
    _.each(this.targetGroups, group => {
      bounds.extend(group.bounds())
    })

    return bounds
  }
}

export default DataStore
